import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock } from "lucide-react";
import { formatUSD, LAUNCH_PRICE } from "../lib/wealthPricing";
import { trackClick, annuityReviewUrl, AUGUSTA_URL } from "../lib/crossSell";
import { AugustaFeatureBlock } from "./wealth/AugustaPromo";

/**
 * Layout for the GetYourWealthReport.website experience.
 * Deliberately distinct from the annuity-review Layout — different brand voice,
 * different palette (deep emerald + cream), tighter conversion-focused chrome.
 */
export default function WealthLayout({ children }) {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex flex-col bg-[#fdfbf6]" data-testid="wealth-layout">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#fdfbf6]/85 border-b border-emerald-900/10" data-testid="wealth-header">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between gap-4">
                    <Link to="/wealth" className="flex items-center gap-3 group" data-testid="wealth-logo-link">
                        <div className="w-10 h-10 rounded-md bg-emerald-900 flex items-center justify-center text-amber-300">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-serif text-xl text-emerald-950 font-semibold tracking-tight">Get Your Wealth Report</div>
                            <div className="uppercase tracking-[0.2em] text-[10px] font-semibold text-emerald-900">getyourwealthreport.com</div>
                        </div>
                    </Link>
                    {/* Subtle secondary nav — never competes with the primary CTA */}
                    <nav className="hidden lg:flex items-center gap-7 text-[13px] font-medium text-emerald-900" data-testid="wealth-secondary-nav">
                        <a
                            href={annuityReviewUrl()}
                            onClick={() => trackClick("annuity_review", { source_page: "wealth_header" })}
                            className="hover:text-emerald-900 transition-colors"
                            data-testid="wealth-nav-annuity-review"
                        >
                            Complimentary Annuity Review
                        </a>
                        <a
                            href={AUGUSTA_URL}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            onClick={() => trackClick("augusta", { source_page: "wealth_header" })}
                            className="hover:text-emerald-900 transition-colors"
                            data-testid="wealth-nav-augusta"
                        >
                            Gold and Silver IRA
                        </a>
                    </nav>
                    <button
                        onClick={() => navigate("/wealth/assessment")}
                        data-testid="wealth-header-cta"
                        className="hidden md:inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-amber-300 px-5 py-2.5 rounded-md font-medium text-sm shadow-sm whitespace-nowrap"
                    >
                        Start Free Assessment
                    </button>
                </div>
            </header>
            <main className="flex-1">{children}</main>
            {/* Augusta partner band — visible on every wealth page (highest-commission affiliate). */}
            <section className="bg-[#fdfbf6] pt-4 pb-12" data-testid="wealth-augusta-band">
                <div className="max-w-5xl mx-auto px-6 sm:px-8">
                    <AugustaFeatureBlock />
                </div>
            </section>
            <footer className="bg-emerald-950 text-emerald-50" data-testid="wealth-footer">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-14">
                    <div className="grid md:grid-cols-3 gap-10">
                        <div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-md bg-amber-300/10 flex items-center justify-center text-amber-300">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="font-serif text-xl">Get Your Wealth Report</div>
                                    <div className="uppercase tracking-[0.2em] text-[10px] text-amber-300/70">getyourwealthreport.com</div>
                                </div>
                            </div>
                            <p className="mt-5 text-emerald-100/80 text-sm leading-relaxed max-w-sm">
                                A 5-minute Retirement Readiness Scorecard&trade; that tells you exactly where your plan stands — and where it doesn't.
                            </p>
                            <div className="mt-5 flex items-center gap-2 text-xs text-emerald-100/60">
                                <Lock className="w-3.5 h-3.5" /> 256-bit secure payment · Powered by Stripe
                            </div>
                        </div>
                        <div>
                            <div className="uppercase tracking-[0.2em] text-xs font-semibold text-amber-300/70 mb-3">Get Started</div>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/wealth" className="text-emerald-100/80 hover:text-white" data-testid="wealth-footer-home">Home</Link></li>
                                <li><Link to="/wealth/assessment" className="text-emerald-100/80 hover:text-white" data-testid="wealth-footer-start">Start My Score</Link></li>
                                <li>
                                    <a
                                        href={annuityReviewUrl()}
                                        onClick={() => trackClick("annuity_review", { source_page: "wealth_footer" })}
                                        className="text-emerald-100/80 hover:text-white"
                                        data-testid="wealth-footer-annuity"
                                    >
                                        Free Annuity Review &rarr;
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href={AUGUSTA_URL}
                                        target="_blank"
                                        rel="noopener noreferrer sponsored"
                                        onClick={() => trackClick("augusta", { source_page: "wealth_footer" })}
                                        className="text-emerald-100/80 hover:text-white"
                                        data-testid="wealth-footer-augusta"
                                    >
                                        Gold and Silver IRA (Educational) &rarr;
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <div className="uppercase tracking-[0.2em] text-xs font-semibold text-amber-300/70 mb-3">Legal</div>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/privacy" className="text-emerald-100/80 hover:text-white">Privacy</Link></li>
                                <li><Link to="/terms" className="text-emerald-100/80 hover:text-white">Terms</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="mt-10 pt-6 border-t border-emerald-900/40 text-xs text-emerald-100/60 leading-relaxed">
                        © {new Date().getFullYear()} Get Your Wealth Report. The Retirement Readiness Scorecard&trade; is an educational tool and not personalized financial advice.
                    </div>
                </div>
            </footer>
        </div>
    );
}
